
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { useTwilioSettings } from "@/hooks/useTwilioSettings";
import { Loader2, Phone, CheckCircle2, ChevronDown } from "lucide-react";

type FieldErrors = Partial<Record<"accountSid" | "authToken" | "phoneNumber" | "messagingServiceSid", string>>;

const normalizePhoneNumber = (value: string) => value.replace(/[\s()-]/g, "");

const TwilioSettings = () => {
  const { settings, loading, updateSettings } = useTwilioSettings();
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [messagingServiceSid, setMessagingServiceSid] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formOpen, setFormOpen] = useState(false);
  const { toast } = useToast();

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!accountSid.trim()) next.accountSid = "Enter your Account SID.";
    else if (!/^AC/i.test(accountSid.trim())) next.accountSid = "Twilio Account SIDs start with \"AC\".";

    if (!authToken.trim()) next.authToken = "Enter your Auth Token.";

    if (!phoneNumber.trim()) next.phoneNumber = "Enter your Twilio phone number.";
    else if (!/^\+[1-9]\d{6,14}$/.test(normalizePhoneNumber(phoneNumber)))
      next.phoneNumber = "Use a full number with country code, like +14155551234.";

    if (messagingServiceSid.trim() && !/^MG/i.test(messagingServiceSid.trim()))
      next.messagingServiceSid = "Messaging Service SIDs start with \"MG\".";

    return next;
  };

  const handleSaveSettings = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast({
        title: "Check the highlighted fields",
        description: "A couple of values need a second look before we can save.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    const success = await updateSettings({
      account_sid: accountSid,
      auth_token: authToken,
      phone_number: normalizePhoneNumber(phoneNumber),
      messaging_service_sid: messagingServiceSid.trim() || null,
      is_active: true
    });

    if (success) {
      setAccountSid("");
      setAuthToken("");
      setPhoneNumber("");
      setMessagingServiceSid("");
      setErrors({});
      setFormOpen(false);
    }
    setIsSaving(false);
  };

  const handleToggleActive = async (checked: boolean) => {
    if (settings) {
      await updateSettings({ is_active: checked });
    }
  };

  const inputClass = (hasError: boolean) =>
    `mt-2 h-11 ${hasError ? "border-status-danger focus-visible:ring-status-danger" : ""}`;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const credentialForm = (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="account-sid" className="text-sm font-medium text-foreground">Account SID</Label>
          <span className="text-xs font-medium text-status-danger">Required</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Found on your Twilio Console dashboard.</p>
        <Input
          id="account-sid"
          type="text"
          inputMode="text"
          autoComplete="off"
          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          value={accountSid}
          onChange={(e) => setAccountSid(e.target.value)}
          aria-invalid={!!errors.accountSid}
          className={inputClass(!!errors.accountSid)}
        />
        {errors.accountSid && <p className="mt-1.5 text-xs text-status-danger">{errors.accountSid}</p>}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="auth-token" className="text-sm font-medium text-foreground">Auth Token</Label>
          <span className="text-xs font-medium text-status-danger">Required</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Stored encrypted. It is never shown again after saving.</p>
        <Input
          id="auth-token"
          type="password"
          autoComplete="off"
          placeholder="Enter your Twilio Auth Token"
          value={authToken}
          onChange={(e) => setAuthToken(e.target.value)}
          aria-invalid={!!errors.authToken}
          className={inputClass(!!errors.authToken)}
        />
        {errors.authToken && <p className="mt-1.5 text-xs text-status-danger">{errors.authToken}</p>}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="twilio-phone-number" className="text-sm font-medium text-foreground">Phone number</Label>
          <span className="text-xs font-medium text-status-danger">Required</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">The Twilio number texts are sent from.</p>
        <Input
          id="twilio-phone-number"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          placeholder="+14155551234"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          aria-invalid={!!errors.phoneNumber}
          className={inputClass(!!errors.phoneNumber)}
        />
        {errors.phoneNumber && <p className="mt-1.5 text-xs text-status-danger">{errors.phoneNumber}</p>}
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="messaging-service-sid" className="text-sm font-medium text-foreground">Messaging Service SID</Label>
          <span className="text-xs text-muted-foreground">Optional</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Enables link shortening in your texts. Recommended.</p>
        <Input
          id="messaging-service-sid"
          type="text"
          autoComplete="off"
          placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          value={messagingServiceSid}
          onChange={(e) => setMessagingServiceSid(e.target.value)}
          aria-invalid={!!errors.messagingServiceSid}
          className={inputClass(!!errors.messagingServiceSid)}
        />
        {errors.messagingServiceSid && <p className="mt-1.5 text-xs text-status-danger">{errors.messagingServiceSid}</p>}
      </div>

      <Button
        onClick={handleSaveSettings}
        disabled={isSaving}
        className="h-11 w-full"
      >
        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {settings ? "Update credentials" : "Save credentials"}
      </Button>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Phone className="h-5 w-5 text-muted-foreground" />
          Twilio
        </CardTitle>
        <CardDescription>
          Connect your Twilio account so live texts can go out.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {settings ? (
          <>
            {/* Status + active toggle */}
            <div className="flex items-start justify-between gap-4 rounded-lg border border-status-success/30 bg-status-success/5 p-4">
              <div className="flex min-w-0 items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-status-success" />
                <div className="min-w-0">
                  <Label className="text-sm font-medium text-foreground">Twilio connected</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {settings.is_active ? "Active and ready for live mode." : "Saved, but currently switched off."}
                  </p>
                </div>
              </div>
              <Switch checked={settings.is_active} onCheckedChange={handleToggleActive} aria-label="Toggle Twilio active" />
            </div>

            {/* Masked summary — no secrets shown */}
            <dl className="grid grid-cols-1 gap-2 text-xs">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Account SID</dt>
                <dd className="truncate font-mono text-foreground">
                  {settings.account_sid.slice(0, 6)}&hellip;{settings.account_sid.slice(-4)}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Phone number</dt>
                <dd className="font-mono text-foreground">{settings.phone_number}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Link shortening</dt>
                <dd className={settings.messaging_service_sid ? "text-status-success" : "text-status-warning"}>
                  {settings.messaging_service_sid ? "On" : "Off"}
                </dd>
              </div>
            </dl>

            {/* Progressive disclosure — the form stays out of the way until needed */}
            <Collapsible open={formOpen} onOpenChange={setFormOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="h-11 w-full justify-between">
                  Update credentials
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${formOpen ? "rotate-180" : ""}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                <p className="mb-4 rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
                  Leave these fields blank to keep your saved credentials. To save new ones, re-enter every required
                  field — a partial entry is rejected and your saved credentials stay as they are.
                </p>
                {credentialForm}
              </CollapsibleContent>
            </Collapsible>
          </>
        ) : (
          credentialForm
        )}

        {/* Setup instructions */}
        <div className="rounded-lg border border-border bg-muted/40 p-4">
          <h4 className="mb-2 text-sm font-medium text-foreground">Where to find these</h4>
          <ol className="list-inside list-decimal space-y-1 text-xs text-muted-foreground">
            <li>Copy the Account SID and Auth Token from the Twilio Console.</li>
            <li>Use a phone number you have verified in Twilio.</li>
            <li>For link shortening, create a Messaging Service and paste its SID.</li>
          </ol>
        </div>
      </CardContent>
    </Card>
  );
};

export default TwilioSettings;
